import { memo, useState, useCallback, useEffect, useRef } from 'react'
import { Map as MapComponent, useMap } from "@vis.gl/react-google-maps/dist/index.umd"
import { TerraDraw, TerraDrawPolygonMode, TerraDrawSelectMode } from "terra-draw";
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter"
import SearchBox from "../common/google-maps/SearchBox"
import { Input } from './Input';
import Spinner from './Spinner';
import { useTranslation } from 'react-i18next';
import { MapSearchBox, MapContainer } from './Gamezone.style';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDrawPolygon, faMousePointer } from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import { Button, MapLoading } from '../App.styles';
import { 
    useCreateZoneMutation, 
    useDeleteZoneMutation, 
    useCreateWaypointMutation, 
    useUpdateWaypointMutation, 
    useDeleteWaypointMutation, 
    useZonesQuery, 
    useInvalidateZonesQuery,
} from './Gamezone.state';
import theme from '../themes';
import ChallengeMarkers from '../ChallengeMarkers';
import NPCMarkers from '../NPCMarkers';

// Styled components for drawing controls and buttons
const DrawingControls = styled.div`
    position: absolute;
    top: 10px;
    left: 190px;
    z-index: 1000;
    display: flex;
    gap: 2px;
    background: white;
    padding: 0;
    border-radius: 2px;
    box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
`;

const ControlButton = styled.button`
    width: 40px;
    height: 40px;
    padding: 8px;
    border: none;
    background: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.active ? theme.infoColor : `rgb(86, 86, 86)`};
    
    &:hover {
        color: ${theme.infoColor};
    }
    
    &:first-child {
        border-top-left-radius: 2px;
        border-bottom-left-radius: 2px;
    }
    
    &:last-child {
        border-top-right-radius: 2px;
        border-bottom-right-radius: 2px;
    }
`;

// Default map center coordinates (Toronto)
const defaultCenter = {
    lat: 43.6426, 
    lng: -79.3871,
}

// Default styling for shapes on the map
const defaultShapeOptions = {
    strokeWeight: 2,
    strokeColor: theme.accent2Color,
    fillColor: theme.accent2Color,
    fillOpacity: 0.35,
}

// Styling for selected shapes
const selectedShapeOptions = {
    strokeWeight: 3,
    strokeColor: theme.accent4Color,
    fillColor: theme.accent4Color,
    fillOpacity: 0.45,
}

// Styling for modified shapes
const modifiedShapeOptions = {
    strokeWeight: 3,
    strokeColor: theme.accent1Color,
    fillColor: theme.accent1Color,
    fillOpacity: 0.45,
}

// Styling for saved shapes
const savedShapeOptions = {
    strokeWeight: 3,
    strokeColor: theme.accent3Color,
    fillColor: theme.accent3Color,
    fillOpacity: 0.45,
}

export function Gamezone({ label = '' }) {
    // Translation hook for internationalization
    const { t } = useTranslation();
    // State for managing selected shape and shape mapping
    const [selectedShape, setSelectedShape] = useState(null);
    const [shapeMap, setShapeMap] = useState([]);
    const [pendingChanges, setPendingChanges] = useState([]);
    const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved
    const [modifiedShapes, setModifiedShapes] = useState(new Set()); // Track modified shapes
    const shapeMapRef = useRef([]);
    const hasLoadedZonesRef = useRef(false);
    const zonesRef = useRef(null);
    const hasFittedInitialBoundsRef = useRef(false);
    const [challengeMarkers, setChallengeMarkers] = useState([]);
    const [npcMarkers, setNpcMarkers] = useState([]);
    // State for search box and map references
    const [searchBox, setSearchBox] = useState(null);
    const [drawingManager, setDrawingManager] = useState(null);
    // State for drawing mode and cancellation
    const [drawingMode, setDrawingMode] = useState('polygon');
    const [isDrawingCancelled, setIsDrawingCancelled] = useState(false);
    const debounceTimeoutRef = useRef(null);
    const map = useMap('google-maps-drawing-gamezone');

    // Get center from last polygon or use default
    const currentCenter = defaultCenter;

    // Mutations and Queries
    const { mutateAsync: createZone } = useCreateZoneMutation();
    const { mutateAsync: deleteZone, isLoading: isDeletingZone } = useDeleteZoneMutation();
    const { mutateAsync: createWaypoint } = useCreateWaypointMutation();
    const { mutateAsync: updateWaypoint } = useUpdateWaypointMutation();
    const { mutateAsync: deleteWaypoint, isLoading: isDeletingWaypoint } = useDeleteWaypointMutation();
    const { data: zones, isFetching: isLoadingZones } = useZonesQuery();
    const reloadZones = useInvalidateZonesQuery();

    const handleChallengeMarkersLoad = useCallback((markers) => {
        setChallengeMarkers(markers);
    }, []);

    const handleNpcMarkersLoad = useCallback((markers) => {
        setNpcMarkers(markers);
    }, []);

    // Extract coordinates from polygon path
    const getPolygonCoordinates = useCallback((polygon) => {
        if (!polygon) return [];
        const path = polygon.getPath();
        return path.getArray().map((latLng, index) => ({
            lat: latLng.lat(),
            lng: latLng.lng(),
            position: index
        }));
    }, []);

    // Update shape coordinates with debouncing
    const updateShapeCoordinates = useCallback((shape) => {
        // Clear any existing timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // Set new timeout
        debounceTimeoutRef.current = setTimeout(async () => {
            const coordinates = getPolygonCoordinates(shape);
            const shapeIndex = shapeMapRef.current.findIndex(map => map.get('polygon') === shape);
            if (shapeIndex !== -1) {
                const shapeData = shapeMapRef.current[shapeIndex];
                const zoneId = shapeData.get('zoneId');
                
                try {
                    // Ensure zonesRef.current is an array
                    const zonesArray = Array.isArray(zonesRef.current) ? zonesRef.current : zonesRef.current?.zones || [];
                    
                    // Find the zone in zonesArray to get existing waypoints
                    const zone = zonesArray.find(z => z.id === zoneId);
                    if (!zone || !zone.waypoints) {
                        return;
                    }

                    // Add to pending changes
                    setPendingChanges(prev => {
                        let newPendingChanges = [...prev];
                        
                        // If the zoneId already exists in the pending changes, update the coordinates
                        if (prev.find(change => change.shapeData.get('zoneId') === zoneId)) {
                            newPendingChanges = prev.map(change => {
                                if (change.shapeData.get('zoneId') === zoneId) {
                                    return { ...change, coordinates: coordinates };
                                }
                                return change;
                            });
                        } else {
                            // If the zoneId does not exist in the pending changes, add a new update
                            newPendingChanges = [...prev, {
                                type: 'update',
                                zoneId: zoneId,
                                shapeData: shapeData,
                                coordinates: coordinates
                            }];
                        }
                        return newPendingChanges;
                    });

                    if (zone.temp) {
                        // Update the shape in the shapeMap
                        setShapeMap(prev => [...prev].map(shapeObj => {
                            if (shapeObj.get('zoneId') === zoneId) {
                                shapeObj.set('polygon', shape);
                            }
                            return shapeObj;
                        }));
                    }

                    // Mark shape as modified for existing zones
                    setModifiedShapes(prev => new Set([...prev, shape]));
                } catch (error) {
                    // Handle error silently
                }
            }
        }, 150); // 150ms debounce delay
    }, [getPolygonCoordinates]);

    const addPolygonListeners = useCallback((polygon) => {
        // Add event listeners for subsequent modifications
        window.google.maps.event.addListener(polygon.getPath(), 'set_at', () => {
            updateShapeCoordinates(polygon);
        });
        window.google.maps.event.addListener(polygon.getPath(), 'insert_at', () => {
            updateShapeCoordinates(polygon);
        });
        window.google.maps.event.addListener(polygon, 'dragend', () => {
            updateShapeCoordinates(polygon);
        });
        
    }, [updateShapeCoordinates]);

    // Ref for Terra Draw finish handler so the listener always calls the latest logic
    const onTerraDrawFinishRef = useRef(null);

    // Handle saving all zones
    const handleSaveAllZones = useCallback(async () => {
        if (pendingChanges.length === 0) return;

        setSaveStatus('saving');
        try {
            for (const change of pendingChanges) {
                if (change.type === 'create') {
                    // Create zone first
                    const zoneData = await createZone({ position: shapeMap.length });
                    
                    // Update the shape data with real zone ID
                    const shapeIndex = shapeMap.findIndex(map => map.get('polygon') === change.shapeData.get('polygon'));
                    if (shapeIndex !== -1) {
                        const updatedShapeData = new Map(change.shapeData);
                        updatedShapeData.set('zoneId', zoneData.id);
                        setShapeMap(prev => {
                            const newMap = [...prev];
                            newMap[shapeIndex] = updatedShapeData;
                            return newMap;
                        });
                    }

                    // Create waypoints
                    for (let i = 0; i < change.coordinates.length; i++) {
                        const coord = change.coordinates[i];
                        const waypointPayload = {
                            zone_id: zoneData.id,
                            position: i,
                            latitude: coord.lat,
                            longitude: coord.lng
                        };
                        await createWaypoint(waypointPayload);
                    }

                    // Reload the zone to get the latest waypoints
                    await reloadZones({
                        ...zoneData,
                        temp: false,
                    });
                } else if (change.type === 'update') {
                    // Ensure zonesRef.current is an array
                    const zonesArray = Array.isArray(zonesRef.current) ? zonesRef.current : zonesRef.current?.zones || [];
                    
                    // Find the zone to get existing waypoints
                    const zone = zonesArray.find(z => z.id === change.zoneId);
                    if (!zone || !zone.waypoints) {
                        continue;
                    }

                    // Update waypoints
                    for (let i = 0; i < change.coordinates.length; i++) {
                        const coord = change.coordinates[i];
                        const existingWaypoint = zone.waypoints.find(wp => wp.position === coord.position);
                        
                        if (existingWaypoint && existingWaypoint.latitude !== coord.lat && existingWaypoint.longitude !== coord.lng) {
                            await updateWaypoint({
                                id: existingWaypoint.id,
                                zone_id: change.zoneId,
                                position: i,
                                latitude: coord.lat,
                                longitude: coord.lng
                            });
                        } else if (!existingWaypoint) {
                            await createWaypoint({
                                zone_id: change.zoneId,
                                position: i,
                                latitude: coord.lat,
                                longitude: coord.lng
                            });
                        }
                    }
                }
            }

            setSaveStatus('saved');

            // Clear pending changes after saving
            setPendingChanges([]);

            // Clear modified shapes after saving
            setModifiedShapes(new Set());

            // Clear selected shape after saving
            setSelectedShape(null);

            // Reset save status after 2 seconds
            setTimeout(() => {
                setSaveStatus('idle');
            }, 2000);
        } catch (error) {
            console.error(t(`Error saving zones:`), error);
            setSaveStatus('idle');
        }
    }, [pendingChanges, shapeMap, createZone, createWaypoint, updateWaypoint, t, reloadZones]);

    // Cleanup debounce timeout on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    // Handles shape selection and styling
    useEffect(() => {
        const handleShapeClick = (shapeData, e) => {
            e.stop();
            if (shapeData === selectedShape) {
                setSelectedShape(null);
            } else {
                setSelectedShape(shapeData);
            }
        };

        shapeMap.forEach(shapeData => {
            const shape = shapeData.get('polygon');

            // Remove existing click listener if any
            window.google.maps.event.clearListeners(shape, 'click');
            
            // Add click listener to select/deselect shape
            window.google.maps.event.addListener(shape, 'click', (e) => handleShapeClick(shapeData, e));
        });

        return () => {
            shapeMap.forEach(shapeData => {
                const shape = shapeData.get('polygon');
                window.google.maps.event.clearListeners(shape, 'click');
            });
        };
    }, [selectedShape, shapeMap]);

    // Apply styling to selected shape
    // A shape will be styled based on the following criteria:
    // 1. If the shape is modified, it will be styled as modified
    // 2. If the shape is saved, it will be styled as saved
    // 3. If the shape is selected, it will be styled as selected
    // 4. Otherwise, it will be styled as default (idle)
    useEffect(() => {
        const applyStyles = (shape, isSelected) => {
            if (!shape) return;
            
            let options;
            if (isSelected) {
                options = selectedShapeOptions;
            } else if (saveStatus === 'saved') {
                options = savedShapeOptions;
            } else if (modifiedShapes.has(shape)) {
                options = modifiedShapeOptions;
            } else {
                options = defaultShapeOptions;
            }

            shape.setOptions({
                strokeWeight: options.strokeWeight,
                strokeColor: options.strokeColor,
                fillColor: options.fillColor,
                fillOpacity: options.fillOpacity,
            });
        };

        shapeMap.forEach(shapeData => {
            const shape = shapeData.get('polygon');
            const zoneId = shapeData.get('zoneId');
            applyStyles(shape, zoneId === selectedShape?.get('zoneId'));
        });
    }, [selectedShape, shapeMap, modifiedShapes, saveStatus]);

    // Initializes the TerraDraw instance and sets up the event listeners for the map
    const initTerraDraw = useCallback(async () => {
        map.addListener("projection_changed", () => {
            const draw = new TerraDraw({
                adapter: new TerraDrawGoogleMapsAdapter({
                lib: window.google.maps,
                map,
                coordinatePrecision: 9,
                }),
                modes: [
                    new TerraDrawSelectMode({
                        flags: {
                            polygon: {
                                feature: {
                                    draggable: true,
                                    rotateable: true,
                                    coordinates: {
                                        midpoints: true,
                                        draggable: true,
                                        deletable: true,
                                    },
                                },
                            },
                            linestring: {
                                feature: {
                                    draggable: true,
                                    rotateable: true,
                                    coordinates: {
                                        midpoints: true,
                                        draggable: true,
                                        deletable: true,
                                    },
                                },
                            },
                            point: {
                                feature: {
                                    draggable: true,
                                    rotateable: true,
                                },
                            },
                            rectangle: {
                                feature: {
                                    draggable: true,
                                    rotateable: true,
                                    coordinates: {
                                        midpoints: true,
                                        draggable: true,
                                        deletable: true,
                                    },
                                },
                            },
                            circle: {
                                feature: {
                                    draggable: true,
                                    rotateable: true,
                                    coordinates: {
                                        midpoints: true,
                                        draggable: true,
                                        deletable: true,
                                    },
                                },
                            },
                            freehand: {
                                feature: {
                                    draggable: true,
                                    rotateable: true,
                                    coordinates: {
                                        midpoints: true,
                                        draggable: true,
                                        deletable: true,
                                    },
                                },
                            },
                        },
                    }),
                    new TerraDrawPolygonMode({
                        editable: true,
                    }),
                ],
            });
            setDrawingManager(draw);
        });   
    }, [map, setDrawingManager]);

    // Handler for Terra Draw polygon finish: add to pendingChanges and show Save button
    useEffect(() => {
        if (!map || !drawingManager) return;

        onTerraDrawFinishRef.current = async (id) => {
            if (isDrawingCancelled) {
                drawingManager.removeFeatures([id]);
                setIsDrawingCancelled(false);
                return;
            }

            const feature = drawingManager.getSnapshotFeature(id);
            if (!feature || feature.geometry.type !== 'Polygon') return;

            const ring = feature.geometry.coordinates[0];
            if (!ring || ring.length < 3) {
                drawingManager.removeFeatures([id]);
                return;
            }

            const coordinates = ring.map(([lng, lat], position) => ({ lat, lng, position }));

            try {
                const polygon = new window.google.maps.Polygon({
                    paths: coordinates.map(c => ({ lat: c.lat, lng: c.lng })),
                    ...defaultShapeOptions,
                    editable: true,
                    draggable: true,
                    map,
                });

                const tempId = `temp_${Date.now()}`;
                const newShapeData = new Map();
                newShapeData.set('polygon', polygon);
                newShapeData.set('zoneId', tempId);

                setPendingChanges(prev => [...prev, {
                    type: 'create',
                    shapeData: newShapeData,
                    coordinates,
                }]);
                setShapeMap(prev => [...prev, newShapeData]);
                setSelectedShape(newShapeData);

                await reloadZones({
                    id: tempId,
                    waypoints: coordinates.map(coord => ({
                        latitude: coord.lat,
                        longitude: coord.lng,
                        position: coord.position,
                    })),
                    temp: true,
                });

                addPolygonListeners(polygon);
                drawingManager.removeFeatures([id]);
            } catch (err) {
                console.error('Error adding polygon from Terra Draw:', err);
                drawingManager.removeFeatures([id]);
            }
        };

        return () => {
            onTerraDrawFinishRef.current = null;
        };
    }, [map, drawingManager, isDrawingCancelled, reloadZones, addPolygonListeners]);

    // On drawing manager ready, set the mode to polygon
    useEffect(() => {
        if (drawingManager) {
            drawingManager.start();

            drawingManager.on('ready', () => {
                drawingManager.setMode('polygon');
            });

            drawingManager.on('finish', (id) => {
                if (onTerraDrawFinishRef.current) {
                    onTerraDrawFinishRef.current(id);
                }
            });
        }
    }, [drawingManager]);

    // Initialize map with existing polygons
    useEffect(() => {
        if (!map) return;

        // Add click listener to map to deselect shapes
        window.google.maps.event.addListener(map, 'click', () => {
            setSelectedShape(null);
        });

        initTerraDraw();
    }, [map, initTerraDraw]);

    // Fit bounds once when map is loaded
    useEffect(() => {
        if (map && !hasFittedInitialBoundsRef.current) {
            if (!isLoadingZones) {
                const bounds = new window.google.maps.LatLngBounds();
                const points = [];

                const zonesArray = Array.isArray(zones) ? zones : zones?.zones || [];
                const waypoints = zonesArray.flatMap(zone => zone.waypoints || []);
                
                if (waypoints.length > 0) {
                    waypoints.forEach(wp => {
                        if (wp.latitude != null && wp.longitude != null) {
                            points.push({ lat: wp.latitude, lng: wp.longitude });
                        }
                    });
                } else if (challengeMarkers.length > 0) {
                    challengeMarkers.forEach(marker => {
                        // Only extend bounds from challenge markers
                        if (marker.lat != null && marker.lng != null) {
                            points.push({ lat: marker.lat, lng: marker.lng });
                        }
                    });
                } else if (npcMarkers.length > 0) {
                    npcMarkers.forEach(marker => {
                        // Only extend bounds from NPC markers
                        if (marker.lat != null && marker.lng != null) {
                            points.push({ lat: marker.lat, lng: marker.lng });
                        }
                    });
                }

                if (points.length > 1) {
                    points.forEach(p => bounds.extend(p));
                    map.fitBounds(bounds);
                } else if (points.length === 1) {
                    // If there is only one point, center the map on it and set the zoom level to 15
                    map.setCenter(points[0]);
                    map.setZoom(15); 
                } else {
                    map.setCenter(defaultCenter);
                }

                if (points.length > 0) {
                    // Set the flag to true only if there are points to bound
                    hasFittedInitialBoundsRef.current = true;
                }
            }
        }
    }, [map, zones, challengeMarkers, npcMarkers, isLoadingZones]);


    // Update map initial bounds when data changes
    useEffect(() => {
        if (zones && !isLoadingZones) {
            // Ensure we're storing the correct data structure
            zonesRef.current = Array.isArray(zones) ? zones : zones?.zones || [];
        }
    }, [zones, isLoadingZones]);


    // Update ref when shapeMap changes
    useEffect(() => {
        shapeMapRef.current = shapeMap;
    }, [shapeMap]);

    // Load existing zones when zones data is available and add event listeners for shape updates
    useEffect(() => {
        if (zonesRef.current && map && !isLoadingZones && !hasLoadedZonesRef.current) {
            // Clear existing shapes from the map
            shapeMapRef.current.forEach(shapeData => {
                const shape = shapeData.get('polygon');
                if (shape) {
                    shape.setMap(null);
                }
            });
            
            // Create new shapes
            const newShapes = [];
            zonesRef.current.forEach(zone => {
                // Only create polygon if zone has waypoints
                if (zone.waypoints && zone.waypoints.length > 0) {
                    try {
                        // Create a new polygon for each zone
                        const polygon = new window.google.maps.Polygon({
                            paths: zone.waypoints.map(wp => ({
                                lat: wp.latitude,
                                lng: wp.longitude
                            })),
                            ...defaultShapeOptions,
                            editable: true,
                            draggable: true,
                            map: map
                        });

                        // Create shape data
                        const shapeData = new Map();
                        shapeData.set('polygon', polygon);
                        shapeData.set('zoneId', zone.id);
  
                        addPolygonListeners(polygon);
  
                        newShapes.push(shapeData);
                    } catch (error) {
                        console.error('Error creating polygon:', error);
                    }
                }
            });

            // Update shape map once with all new shapes
            setShapeMap(newShapes);
            hasLoadedZonesRef.current = true;
        }

        // Cleanup function
        return () => {
            shapeMapRef.current.forEach(shapeData => {
                const shape = shapeData.get('polygon');
                if (shape) {
                    shape.setMap(null);
                    // Clear all event listeners
                    window.google.maps.event.clearInstanceListeners(shape);
                }
            });
            hasLoadedZonesRef.current = false;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, isLoadingZones, addPolygonListeners]);

    // Remove selected shape from map
    const deleteSelectedShape = useCallback(async () => {
                
        try {
            if (selectedShape) {
                const shapeIndex = shapeMap.findIndex(map => map.get('polygon') === selectedShape.get('polygon'));
                if (shapeIndex !== -1) {
                    const shapeData = shapeMap[shapeIndex];
                    const zoneId = shapeData.get('zoneId');
                    const polygon = shapeData.get('polygon');

                    // Find the zone to get its waypoints
                    const zonesArray = Array.isArray(zones) ? zones : zones?.zones || [];
                    const zone = zonesArray.find(z => z.id === zoneId);
                    if (zone && zone.waypoints && !zone.temp) {
                        // Delete all waypoints first
                        for (const waypoint of zone.waypoints) {
                            await deleteWaypoint({
                                id: waypoint.id,
                                zone_id: zoneId,
                            });
                        }
                    }

                    // Remove the shape from the map
                    polygon.setMap(null);
                    
                    // Delete the zone
                    if (!zone.temp) {
                        await deleteZone(zoneId);
                    }
                    
                    // Update the shape map
                    setShapeMap(prev => prev.filter((_, index) => index !== shapeIndex));
                    
                    // Clear any pending changes for this zone
                    setPendingChanges(prev => {
                        const newPendingChanges = prev.filter(change => {
                            if (!change || !change.shapeData) return false;
                            const changeZoneId = change.shapeData.get('zoneId');
                            return changeZoneId !== zoneId;
                        });
                        return newPendingChanges;
                    });
                    
                    // Remove from modified shapes
                    setModifiedShapes(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(selectedShape);
                        return newSet;
                    });
                    
                    setSelectedShape(null);
                }
            }
        } catch (error) {
            console.error(t(`Error deleting zone and waypoints:`), error);
        }
    }, [selectedShape, zones, shapeMap, deleteZone, deleteWaypoint, t]);

    // Initialize search box reference
    const onSearchBoxLoad = useCallback((ref) => {
        setSearchBox(ref);
    }, []);

    // Handle place selection from search box
    const onPlacesChanged = useCallback(() => {
        if (searchBox && map) {
            const places = searchBox.getPlaces();
            if (places.length === 0) return;

            const place = places[0];
            if (!place.geometry || !place.geometry.location) return;

            // Pan map to the selected place
            map.panTo(place.geometry.location);
        }
    }, [searchBox, map]);

    // Handle drawing cancellation
    useEffect(() => {
        if (drawingManager) {
            drawingManager.setMode(drawingMode);
        }
    }, [drawingManager, drawingMode]);

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && drawingManager && drawingMode === 'polygon') {
                // Cancel in-progress polygon by switching to select, then back to polygon
                setIsDrawingCancelled(true);
                drawingManager.setMode('select');
                setDrawingMode('polygon');
                drawingManager.setMode('polygon');
            }
            // Delete selected zone when Delete key is pressed
            if (['Backspace', 'Delete'].includes(event.key) && selectedShape) {
                deleteSelectedShape();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [drawingManager, drawingMode, selectedShape, deleteSelectedShape]);

    return (
        <>
            <div className='form-outline'>
                <label className="form-label">{label}</label>
                <div className='map'>
                    <div className='map-container'>
                        <MapContainer>
                            <MapComponent
                                id='google-maps-drawing-gamezone'
                                mapId='google-maps-drawing-gamezone'
                                mapContainerStyle={{ width: "100%", height: "100%" }}
                                defaultZoom={13}
                                defaultCenter={currentCenter}
                                options={{
                                    mapTypeControl: true,
                                    mapTypeControlOptions: {
                                        position: window.google?.maps?.ControlPosition?.TOP_LEFT,
                                    },
                                    fullscreenControl: false,
                                }}
                            >
                                {(isLoadingZones) && (
                                    <MapLoading>
                                        {t(`Loading zones...`)}
                                    </MapLoading>
                                )}
                                <MapSearchBox>
                                    <SearchBox
                                        onLoad={onSearchBoxLoad}
                                        onPlacesChanged={onPlacesChanged}
                                    >
                                        <Input
                                            type="text"
                                            label={t(`Search locations...`)}
                                        />
                                    </SearchBox>
                                </MapSearchBox>
                                <DrawingControls>
                                    <ControlButton
                                        onClick={() => setDrawingMode('polygon')}
                                        active={drawingMode === 'polygon'}
                                        title="Draw polygon"
                                    >
                                        <FontAwesomeIcon icon={faDrawPolygon} size="lg" />
                                    </ControlButton>
                                    <ControlButton
                                        onClick={() => setDrawingMode('select')}
                                        active={drawingMode === 'select'}
                                        title="Select and edit"
                                    >
                                        <FontAwesomeIcon icon={faMousePointer} size="lg" />
                                    </ControlButton>
                                </DrawingControls>
                                <ChallengeMarkers onMarkersLoaded={handleChallengeMarkersLoad} />
                                <NPCMarkers onMarkersLoaded={handleNpcMarkersLoad} />
                            </MapComponent>
                        </MapContainer>
                        <div className="d-flex gap-2">
                            {selectedShape && (
                                <Button 
                                    color={isDeletingZone || isDeletingWaypoint ? 'warning' : 'danger'}
                                    onClick={deleteSelectedShape}
                                    disabled={isDeletingZone || isDeletingWaypoint}
                                >
                                    <span>
                                        {
                                            (isDeletingZone || isDeletingWaypoint) 
                                                ? <>{t(`Deleting...`)} <Spinner size="0.875rem" /></>
                                                : t(`Delete zone`)}
                                    </span>
                                </Button>
                            )}
                            {pendingChanges.length > 0 && (
                                <Button 
                                    color={
                                        saveStatus === 'idle' ? 'primary' :
                                        saveStatus === 'saving' ? 'warning' :
                                        'success'
                                    }
                                    onClick={handleSaveAllZones}
                                    disabled={saveStatus === 'saving' || saveStatus === 'saved'}
                                >
                                    <span>
                                        {saveStatus === 'idle' && t(`Save all zones`)}
                                        {saveStatus === 'saving' && <>{t(`Saving...`)} <Spinner size="0.875rem" /></>}
                                        {saveStatus === 'saved' && t(`Zones saved`)}
                                    </span>
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default memo(Gamezone)